import { ReactNode } from 'react'
import { Helmet } from 'react-helmet-async'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import { Box, Button, Columns } from '@domain-group/suit-theme'

import ImpersonateEntityButton from '@components/ImpersonateEntityButton'
import PageHeader from '@components/PageHeader'
import ScopedConditionalContent from '@components/ScopedConditionalContent'

import useAgency from '@hooks/useAgency'
import useCampaign from '@hooks/useCampaign'
import useCurrentUser from '@hooks/useCurrentUser'
import useOrderItems from '@hooks/useOrderItems'
import useSelector from '@hooks/useSelector'

import generateTabBarItems from '../../utils/generateTabBarItems'

interface PageLayoutProps {
  activeTabBarItemKey: string,
  children: ReactNode,
  entityPayload: ReturnType<typeof useCampaign>,
}

const PageLayout = (props: PageLayoutProps) => {
  const { activeTabBarItemKey, children, entityPayload } = props

  const {
    campaign: { address, agency_id, paths },
    campaign,
    urls: { campaignEditDashboardUrl, campaignsIndexUrl },
  } = entityPayload

  const { isProviderUser } = useCurrentUser()

  const agencyPayload = useAgency({ id: agency_id })
  const { agency, callbacks: { loginAsAgency } } = agencyPayload

  const { services } = useSelector(reduxState => reduxState.entities)
  const copyWritingService = Object.values(services).find(service => service.short_name === 'adcopy')

  const orderItemsPayload = useOrderItems({
    filters: {
      campaign_id: campaign.id,
    },
  })
  const { filteredOrderItems } = orderItemsPayload
  const showCopywritingTab = filteredOrderItems.some(orderItem => orderItem.service_id === copyWritingService?.id)

  const { redirect }: { redirect?: string } = parsePermittedQueryParams(getQueryParamsFromHash(), ['redirect'])

  return (
    <>
      <Helmet>
        <title>{address || 'Campaign'} | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <>
            <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
              <ImpersonateEntityButton
                // @ts-expect-error TS2322
                callbacks={{ loginAsEntity: loginAsAgency }}
                // @ts-expect-error TS2322
                entity={agency}
                marginRight="medium"
                redirectPath={campaignEditDashboardUrl}
                requestOptions={{
                  redirect: paths?.edit,
                }}
              >
                Login as Agency
              </ImpersonateEntityButton>
            </ScopedConditionalContent>

            <Button
              // @ts-expect-error TS2322
              as="a"
              buttonStyle="secondaryUtility"
              href={redirect || campaignsIndexUrl}
              size="medium"
              width="auto"
            >
              Back
            </Button>
          </>
        )}
        activeTabBarItemKey={activeTabBarItemKey}
        tabBarItems={generateTabBarItems({
          campaign,
          isProviderUser,
          showCopywritingTab,
        })}
        title="Edit Campaign"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          {children}
        </Columns>
      </Box>
    </>
  )
}

export default PageLayout
